<template>
  <label class="label text-xl sm:text-2xl py-0 mb-2 sm:mb-3.5 field-name-label">
    <MarkdownContent
      v-if="field.title"
      :string="field.title"
    />
    <template v-else>{{ field.name || t('identity_verification') }}</template>
  </label>
  <div
    v-if="field.description"
    dir="auto"
    class="mb-4 px-1 field-description-text"
  >
    <MarkdownContent :string="field.description" />
  </div>
  <div
    v-if="isRequiredFieldEmpty"
    class="px-1 field-description-text"
  >
    {{ t('complete_all_required_fields_to_proceed_with_identity_verification') }}
  </div>
  <div v-else>
    <div
      v-if="isLoading"
      class="w-full flex space-x-2 justify-center mb-2"
    >
      <IconInnerShadowTop
        width="40"
        class="animate-spin h-10"
      />
    </div>
    <div v-else-if="eideasyMode">
      <div
        ref="eideasyContainer"
        class="eideasy-widget-container"
      />
    </div>
    <div v-else-if="redirectUrl">
      <a
        :href="redirectUrl"
        target="_blank"
        class="white-button w-full"
      >
        {{ t('verify_id') }}
      </a>
    </div>
    <div
      v-if="error"
      class="mb-4 text-center text-red-600"
    >
      {{ error }}
    </div>
  </div>
</template>

<script>
import MarkdownContent from './markdown_content'
import { IconInnerShadowTop } from '@tabler/icons-vue'

export default {
  name: 'VerificationStep',
  components: {
    MarkdownContent,
    IconInnerShadowTop
  },
  inject: ['baseUrl', 't'],
  props: {
    modelValue: {
      type: String,
      required: false,
      default: ''
    },
    submitter: {
      type: Object,
      required: true
    },
    field: {
      type: Object,
      required: true
    },
    emptyValueRequiredStep: {
      type: Object,
      required: false,
      default: null
    },
    values: {
      type: Object,
      required: true
    },
    locale: {
      type: String,
      required: false,
      default: 'en'
    },
    submitterSlug: {
      type: String,
      required: true
    }
  },
  emits: ['focus', 'submit', 'update:model-value', 'attached'],
  data () {
    return {
      redirectUrl: '',
      isLoading: false,
      verificationData: {},
      error: null,
      pollInterval: null,
      eideasyMode: false
    }
  },
  computed: {
    isRequiredFieldEmpty () {
      return this.emptyValueRequiredStep && this.emptyValueRequiredStep[0] !== this.field
    }
  },
  async mounted () {
    if (!this.isRequiredFieldEmpty) {
      this.isLoading = true

      if (new URLSearchParams(window.location.search).get('submit') === 'true') {
        this.$emit('submit')
      } else {
        await this.start()
        this.isLoading = false
      }
    }
  },
  beforeUnmount () {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
    }
  },
  methods: {
    async start () {
      try {
        const resp = await fetch(this.baseUrl + `/api/identity_verification/${this.field.uuid}`, {
          method: 'PUT',
          body: JSON.stringify({
            submitter_slug: this.submitterSlug,
            redirect_url: document.location.href
          }),
          headers: { 'Content-Type': 'application/json' }
        })

        this.verificationData = await resp.json()

        if (this.verificationData.check_completed) {
          this.$emit('submit')
          return
        }

        if (this.verificationData.eideasy_widget) {
          this.eideasyMode = true
          this.$nextTick(() => {
            this.initEidEasyWidget(this.verificationData)
          })
          return
        }

        if (this.verificationData.redirect_url) {
          this.redirectUrl = this.verificationData.redirect_url

          this.pollInterval = setInterval(async () => {
            try {
              const checkResp = await fetch(this.baseUrl + '/api/identity_verification', {
                method: 'POST',
                body: JSON.stringify({ submitter_slug: this.submitterSlug }),
                headers: { 'Content-Type': 'application/json' }
              })

              if (checkResp.ok) {
                clearInterval(this.pollInterval)
                this.$emit('submit')
              }
            } catch (e) {
              console.error('Poll error:', e)
            }
          }, 5000)
        }
      } catch (e) {
        this.error = e.message
      }
    },
    initEidEasyWidget (data) {
      import('@eid-easy/eideasy-widget').then(() => {
        const container = this.$refs.eideasyContainer
        if (!container) return

        const widget = document.createElement('eideasy-widget')
        widget.setAttribute('client-id', data.eideasy_client_id)
        widget.setAttribute('doc-id', data.eideasy_doc_id)
        widget.setAttribute('country-code', data.eideasy_country || '')
        widget.setAttribute('language', this.locale || 'en')
        widget.setAttribute('sandbox', data.eideasy_sandbox === true ? 'true' : 'false')

        widget.setAttribute('signature-methods-show', 'all')
        widget.setAttribute('identification-methods-show', '')

        widget.addEventListener('signing-complete', () => {
          this.startPolling()
        })

        widget.addEventListener('signing-failed', (e) => {
          this.error = (e && e.detail && e.detail.message) || 'Signing failed. Please try again.'
        })

        widget.addEventListener('signing-cancelled', () => {
          this.error = 'Signing was cancelled. Please try again.'
        })

        container.innerHTML = ''
        container.appendChild(widget)
      }).catch((err) => {
        console.error('Failed to load eIDEasy widget:', err)
        this.error = 'Failed to load signing widget. Please refresh the page.'
      })
    },
    startPolling () {
      this.pollInterval = setInterval(async () => {
        try {
          const checkResp = await fetch(this.baseUrl + '/api/identity_verification', {
            method: 'POST',
            body: JSON.stringify({ submitter_slug: this.submitterSlug }),
            headers: { 'Content-Type': 'application/json' }
          })

          if (checkResp.ok) {
            const data = await checkResp.json()
            if (data.status === 'verified' || data.check_completed) {
              clearInterval(this.pollInterval)
              this.$emit('submit')
            }
          }
        } catch (e) {
          console.error('Poll error:', e)
        }
      }, 3000)
    },
    async submit () {
      const resp = await fetch(this.baseUrl + '/api/identity_verification', {
        method: 'POST',
        body: JSON.stringify({
          submitter_slug: this.submitterSlug
        }),
        headers: { 'Content-Type': 'application/json' }
      })

      if (resp.status === 404) {
        throw new Error('Verification not completed yet')
      }

      if (resp.ok) {
        return resp
      } else {
        throw new Error('Verification failed')
      }
    }
  }
}
</script>
